/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { subscribeToAuthChanges } from '../services/authService';
import { getTeamByUserId, subscribeToTeamChanges } from '../services/teamService';

// Create context
const AuthContext = createContext(null);

/**
 * Authentication Provider Component
 * Wraps the app to provide auth state and methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Fetch team data initially when user is authenticated
        const result = await getTeamByUserId(firebaseUser.uid);
        if (result.success) {
          setTeamData(result.team);
        }
      } else {
        setTeamData(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Subscribe to real-time team data changes
  useEffect(() => {
    let unsubscribeTeam = null;

    if (user) {
      // Set up real-time listener for team document
      // Use user.uid as the document ID (teams are stored as teams/userId)
      unsubscribeTeam = subscribeToTeamChanges(user.uid, (updatedTeam) => {
        if (updatedTeam) {
          setTeamData(updatedTeam);
        }
      });
    }

    // Cleanup team subscription
    return () => {
      if (unsubscribeTeam) {
        unsubscribeTeam();
      }
    };
  }, [user?.uid]);

  /**
   * Update team data in context
   * Used after submissions or profile updates
   */
  const updateTeamData = (newData) => {
    setTeamData(prev => ({ ...prev, ...newData }));
  };

  /**
   * Refresh team data from database
   */
  const refreshTeamData = async () => {
    if (user) {
      const result = await getTeamByUserId(user.uid);
      if (result.success) {
        setTeamData(result.team);
      }
    }
  };

  /**
   * Clear auth error
   */
  const clearError = () => setError(null);

  // Context value
  const value = {
    user,
    teamData,
    loading,
    error,
    isAuthenticated: !!user,
    updateTeamData,
    refreshTeamData,
    clearError,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;
